/**
 * Notification-preference matrix data layer — TanStack Query hooks over the gated
 * B4.4 endpoint (`/tasks/notification-prefs`). This is the ONLY place the matrix
 * talks to the server; the rendering component (`<NotificationPrefsMatrix/>`)
 * consumes these hooks and never calls `apiFetch` directly — the same query ⟂
 * view split the rest of taskmap keeps (§A7).
 *
 * Issue #1043 (B4.4, wireframe `V_B44`). The matrix is "what to nudge me about,
 * where": seven Map EVENTS x three CHANNELS (IN-APP · EMAIL · DIGEST). It is the
 * member's OWN, always-reversible choice (§A9) — the backend resolves the caller's
 * own membership and exposes no `member_id` parameter, so there is no way to read
 * or write anyone else's matrix.
 *
 * EMAIL + DIGEST are STORED preferences only — honoured when those delivery
 * channels ship as their own issue (founder revenue-first gating). IN-APP is the
 * only live channel today (the backend's `generate_for_note` honours it for the
 * `mentions` + `notes_on_my_tasks` events). The UI labels EMAIL/DIGEST honestly.
 *
 * Gating: this hook is only *consumed* behind `task_notification_prefs` (off by
 * default, §A10); the backend enforces the gate too. The query is `enabled` only
 * when the surface is actually showing, and degrades calmly on a 404 (`retry:
 * false`) so an undeployed/ungated endpoint never crashes.
 *
 * Types are declared LOCALLY (not pulled from `schema.gen.ts`) to avoid the
 * route-shape regen cascade flagged in the orchestrator handoff — the same stance
 * `usePatchTaskNode` took for `target_date`. The shapes mirror the backend's
 * `NotificationPrefsResponse` / `SaveNotificationPrefsRequest` exactly.
 *
 * Isolation: imports only the generic `apiFetch` — no scheduler coupling, no
 * rendering.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";

/** A single event's per-channel preference. */
export interface EventChannelPrefs {
  inapp: boolean;
  email: boolean;
  digest: boolean;
}

/** The full matrix: event_type → per-channel prefs. */
export type NotificationMatrix = Record<string, EventChannelPrefs>;

/** The resolved-matrix response (mirrors the backend `NotificationPrefsResponse`). */
export interface NotificationPrefsResponse {
  /** The complete resolved matrix (stored prefs merged over the calm defaults). */
  matrix: NotificationMatrix;
  /** The event types in wireframe row order. */
  events: string[];
  /** The channels in wireframe column order (`inapp`, `email`, `digest`). */
  channels: string[];
}

/** Cache key for the matrix. Per-team — the matrix is per-member-in-team. */
export const NOTIFICATION_PREFS_KEY = (teamId: string): readonly string[] => [
  "task-notification-prefs",
  teamId,
];

const STALE_TIME_MS = 60_000;

function prefsUrl(teamId: string): string {
  return `/api/teams/${teamId}/tasks/notification-prefs`;
}

/**
 * Fetch the caller's own resolved matrix. `enabled` only when both the team id is
 * present AND the caller opts in (`active` — the matrix surface is open and the
 * layer is on), so no request fires for a closed dialog. Degrades calmly on a 404
 * (`retry: false`) — an undeployed / ungated endpoint shows the calm error state
 * rather than spinning.
 */
export function useNotificationPrefs(teamId: string | undefined, active: boolean) {
  return useQuery({
    queryKey: NOTIFICATION_PREFS_KEY(teamId ?? ""),
    enabled: Boolean(teamId) && active,
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<NotificationPrefsResponse>(prefsUrl(teamId as string)),
  });
}

/**
 * Save the caller's matrix (PUT). The FE always sends the FULL matrix behind the
 * explicit "save" button (the backend replaces the stored blob WHOLE). On success
 * the authoritative resolved response is written straight into the query cache so
 * the grid reflects the saved state without a refetch.
 */
export function useSaveNotificationPrefs(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matrix: NotificationMatrix) =>
      apiFetch<NotificationPrefsResponse>(prefsUrl(teamId as string), {
        method: "PUT",
        body: JSON.stringify({ matrix }),
      }),
    onSuccess: (data) => {
      qc.setQueryData(NOTIFICATION_PREFS_KEY(teamId ?? ""), data);
    },
  });
}
